import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { getRouteApi } from "@tanstack/react-router"
import {
  ChevronDown,
  CircleCheck,
  FileImage,
  FileText,
  Flag,
  Maximize2,
  Minus,
  Plus,
} from "lucide-react"
import { lazy, Suspense, useId, useRef, useState, type ReactNode } from "react"

import {
  Button,
  cn,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  FieldError,
  FieldLabel,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  toast,
} from "@atlant/ui"

import { ErrorBoundary } from "#components/errors/error-boundary.tsx"
import { graphql } from "#gql"
import { invoiceCorrectionKeys, queryKeys } from "#lib/query-keys.ts"
import { useLoggedInUserInfo } from "#store/user-info.ts"
import { DateConverter } from "#utils/date.ts"
import { getFileUrl } from "#utils/file-url.ts"
import { client } from "#utils/graphql-client.ts"
import { formatAmount } from "#utils/money.ts"

import type { CategoryGroup, InboxInvoice } from "../page.tsx"
import type { InvoiceFile } from "./invoice-preview.tsx"
import { InvoiceTypeBadge } from "./invoice-type-badge.tsx"

// pdf.js is large: it loads when the first invoice is opened, not with the month page
const InvoicePreview = lazy(() =>
  import("./invoice-preview.tsx").then(({ InvoicePreview }) => ({
    default: InvoicePreview,
  })),
)

const InvoiceCategoriesDocument = graphql(/* GraphQL */ `
  query InvoiceCategories($input: QueryInvoiceCategoriesGetInput!) {
    invoiceCategoriesGet(input: $input) {
      id
      code
      description
    }
  }
`)

const InvoiceLegCodesDocument = graphql(/* GraphQL */ `
  query InvoiceLegCodes($input: QueryInvoiceLegCodesInput!) {
    invoiceLegCodes(input: $input) {
      code
      tails
      arrival {
        displaydate
      }
    }
  }
`)

const InvoiceUpdateDocument = graphql(/* GraphQL */ `
  mutation InvoiceUpdate($input: MutationInvoiceUpdateInput!) {
    invoiceUpdate(input: $input) {
      id
    }
  }
`)

// 1 fits the file to the preview's width
const ZOOM_STEPS = [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2]
const FIT_WIDTH = ZOOM_STEPS.indexOf(1)

const PREVIEW_HEIGHT = "h-[28rem]"

const route = getRouteApi("/_authenticated/$slug/invoices/$period")

const kindLabels = {
  INVOICE: "Invoice",
  CREDIT_NOTE: "Credit note",
  SUPPORTING: "Supporting document",
} as const

interface InvoiceDetailProps {
  invoice?: InboxInvoice
  group?: CategoryGroup
}

export const InvoiceDetail = ({ invoice, group }: InvoiceDetailProps) => (
  <div className="bg-card flex min-h-80 flex-col rounded-lg border lg:min-h-0">
    {invoice ? (
      // Keyed, so zoom, the chosen file and More details start over for each invoice
      <SelectedInvoice key={invoice.id} invoice={invoice} group={group} />
    ) : (
      <>
        <PanelHeader />
        <div className="flex flex-1 flex-col items-center justify-center gap-2 p-4 text-center">
          <span className="text-xl font-semibold">Select an invoice</span>
          <span className="text-muted-foreground text-sm">
            Expand a category and select an invoice to view its details.
          </span>
        </div>
      </>
    )}
  </div>
)

const PanelHeader = ({ children }: { children?: ReactNode }) => (
  <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3">
    <span className="text-sm font-semibold">Invoice Detail</span>
    {children}
  </div>
)

const SelectedInvoice = ({
  invoice,
  group,
}: {
  invoice: InboxInvoice
  group?: CategoryGroup
}) => {
  const { me } = useLoggedInUserInfo()
  const [zoomStep, setZoomStep] = useState(FIT_WIDTH)
  const [fileIndex, setFileIndex] = useState(0)
  const [fullScreen, setFullScreen] = useState(false)

  // The invoice's own file first, then its supporting documents
  const file = invoice.files[fileIndex]
  // Built once here: the preview loads it, and the dialog title and fallback link to it
  const fileUrl =
    file && getFileUrl(me.organization.id, "invoice", file.blobName, "private")
  const zoom = ZOOM_STEPS[zoomStep]!
  const zoomControls = <ZoomControls step={zoomStep} onChange={setZoomStep} />
  const edit = useInvoiceEdit(invoice, group)

  return (
    <>
      <PanelHeader>
        {file && (
          <div className="flex items-center gap-2">
            {zoomControls}
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label="Full screen"
              onClick={() => setFullScreen(true)}
            >
              <Maximize2 />
            </Button>
          </div>
        )}
      </PanelHeader>

      <div className="flex flex-col gap-4 overflow-auto px-4 pb-4">
        {invoice.files.length > 1 && (
          <FileChips files={invoice.files} selected={fileIndex} onSelect={setFileIndex} />
        )}

        {file && fileUrl ? (
          <PreviewBox
            key={file.id}
            file={file}
            url={fileUrl}
            zoom={zoom}
            // Hidden behind full screen, it would draw the same pages a second time
            paused={fullScreen}
            className={cn(PREVIEW_HEIGHT, "shrink-0")}
          />
        ) : (
          // Seeded and hand-entered invoices have no uploaded file
          <div className="text-muted-foreground flex h-32 shrink-0 items-center justify-center rounded-lg border border-dashed text-sm">
            No file attached
          </div>
        )}

        <ParsedInvoiceData invoice={invoice} group={group} edit={edit} />
        <MoreDetails invoice={invoice} />
        <div className="grid grid-cols-2 gap-3 border-t px-4 py-3">
          {/* Neither anomaly flagging nor confirming a match is ticketed yet; edits save as made */}
          <Button variant="outline" disabled className="text-destructive">
            <Flag />
            Flag as anomaly
          </Button>
          <Button disabled className="bg-emerald-600 text-white hover:bg-emerald-700">
            <CircleCheck />
            Confirm match
          </Button>
        </div>
      </div>

      {file && fileUrl && (
        <Dialog open={fullScreen} onOpenChange={setFullScreen}>
          <DialogContent
            aria-describedby={undefined}
            className="flex h-[90vh] flex-col sm:max-w-[min(95vw,72rem)]"
          >
            <DialogHeader className="flex-row items-center justify-between gap-4 pr-10">
              <DialogTitle className="min-w-0 truncate">
                <a
                  href={fileUrl}
                  target="_blank"
                  rel="noreferrer"
                  title={file.fileName}
                  // Default cursor, as on the upload queue's file links
                  className="cursor-default hover:underline"
                >
                  {file.fileName}
                </a>
              </DialogTitle>
              {zoomControls}
            </DialogHeader>
            <PreviewBox
              key={file.id}
              file={file}
              url={fileUrl}
              zoom={zoom}
              className="min-h-0 flex-1"
            />
          </DialogContent>
        </Dialog>
      )}
    </>
  )
}

/**
 * The lazy viewer, kept to its box when it fails. Its chunk can be gone after a deploy (an
 * old tab) or lost on a bad connection, and Suspense catches neither.
 */
const PreviewBox = ({
  file,
  url,
  zoom,
  paused,
  className,
}: {
  file: InvoiceFile
  url: string
  zoom: number
  paused?: boolean
  className: string
}) => (
  <ErrorBoundary fallback={<PreviewUnavailable url={url} className={className} />}>
    <Suspense fallback={<Skeleton className={className} />}>
      <InvoicePreview
        file={file}
        url={url}
        zoom={zoom}
        paused={paused}
        className={className}
      />
    </Suspense>
  </ErrorBoundary>
)

// React.lazy remembers a failed import, so only a reload fetches the chunk again
const PreviewUnavailable = ({ url, className }: { url: string; className: string }) => (
  <div
    className={cn(
      "text-muted-foreground flex flex-col items-center justify-center gap-3 rounded-lg border text-sm",
      className,
    )}
  >
    <span>Couldn't load the preview.</span>
    <div className="flex items-center gap-3">
      <Button variant="outline" size="sm" onClick={() => window.location.reload()}>
        Reload page
      </Button>
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        className="text-primary cursor-default hover:underline"
      >
        Open the file in a new tab
      </a>
    </div>
  </div>
)

const ZoomControls = ({
  step,
  onChange,
}: {
  step: number
  onChange: (step: number) => void
}) => {
  const canZoomOut = step > 0
  const canZoomIn = step < ZOOM_STEPS.length - 1

  // aria-disabled rather than disabled: a disabled button drops keyboard focus mid-zoom
  return (
    <div className="flex items-center gap-1">
      <Button
        variant="ghost"
        size="icon-sm"
        aria-label="Zoom out"
        aria-disabled={!canZoomOut}
        onClick={() => canZoomOut && onChange(step - 1)}
        className="aria-disabled:opacity-50"
      >
        <Minus />
      </Button>
      <span aria-live="polite" className="w-11 text-center text-sm tabular-nums">
        {ZOOM_STEPS[step]! * 100}%
      </span>
      <Button
        variant="ghost"
        size="icon-sm"
        aria-label="Zoom in"
        aria-disabled={!canZoomIn}
        onClick={() => canZoomIn && onChange(step + 1)}
        className="aria-disabled:opacity-50"
      >
        <Plus />
      </Button>
    </div>
  )
}

/** Switches the preview between an invoice's files; shown when it has more than one. */
const FileChips = ({
  files,
  selected,
  onSelect,
}: {
  files: InvoiceFile[]
  selected: number
  onSelect: (index: number) => void
}) => {
  // Directo names all start alike, so the chips name the kind and number repeats
  const kinds = files.map((file) => file.documentKind ?? "INVOICE")
  const labels = kinds.map((kind, i) => {
    const sameKind = kinds.filter((other) => other === kind).length
    const nth = kinds.slice(0, i + 1).filter((other) => other === kind).length
    return sameKind > 1 ? `${kindLabels[kind]} ${nth}` : kindLabels[kind]
  })

  return (
    <div role="group" aria-label="Files" className="flex flex-wrap gap-1.5">
      {files.map((file, i) => {
        const Icon = file.mimeType === "application/pdf" ? FileText : FileImage
        return (
          <Button
            key={file.id}
            variant={i === selected ? "secondary" : "outline"}
            size="sm"
            aria-pressed={i === selected}
            title={file.fileName}
            onClick={() => onSelect(i)}
          >
            <Icon />
            {labels[i]}
          </Button>
        )
      })}
    </div>
  )
}

type InvoiceEdit = ReturnType<typeof useInvoiceEdit>

type InvoiceChanges = Partial<Record<"vendor" | "categoryId" | "legCode", string>>

/** Vendor, category and leg code edits, each saved as made: the vendor on blur or Enter, a select on pick. */
const useInvoiceEdit = (invoice: InboxInvoice, group?: CategoryGroup) => {
  const { me } = useLoggedInUserInfo()
  const queryClient = useQueryClient()
  const [vendorDraft, setVendor] = useState<string | null>(null)
  const [categoryDraft, setCategory] = useState<string | null>(null)
  const [legCodeDraft, setLegCode] = useState<string | null>(null)

  const vendor = vendorDraft ?? invoice.vendor
  const categoryId = categoryDraft ?? group?.categoryId ?? undefined
  const legCode = legCodeDraft ?? invoice.legCode ?? undefined
  const vendorError = vendor.trim() ? undefined : "Vendor can't be empty"

  // The vendor sent last, until its save settles: what the server will hold
  const sentVendor = useRef<string>(undefined)

  const { mutate } = useMutation({
    mutationFn: (fields: InvoiceChanges) =>
      client.request(InvoiceUpdateDocument, {
        input: { id: invoice.id, organizationId: me.organization.id, ...fields },
      }),
    onMutate: ({ vendor }) => {
      if (vendor !== undefined) sentVendor.current = vendor
    },
    onSettled: (_, _error, { vendor }) => {
      if (vendor === sentVendor.current) sentVendor.current = undefined
    },
    onSuccess: async (_, fields) => {
      // Refetched before the drafts clear, so the fields never flash the old values
      await Promise.all(
        invoiceCorrectionKeys().map((queryKey) =>
          queryClient.invalidateQueries({ queryKey }),
        ),
      )
      // Only the saved fields: another may have changed while this one saved
      if (fields.vendor !== undefined) setVendor(null)
      if (fields.categoryId !== undefined) setCategory(null)
      if (fields.legCode !== undefined) setLegCode(null)
      toast.success("Invoice updated")
    },
    // A failed pick shows the saved value again, so it can be picked again
    onError: (_, { categoryId, legCode }) => {
      setCategory((draft) => (draft === categoryId ? null : draft))
      setLegCode((draft) => (draft === legCode ? null : draft))
    },
  })

  // Radix fires this only on a change; picking the saved value back undoes a save in flight
  const select =
    (field: "categoryId" | "legCode", setDraft: (value: string) => void) =>
    (value: string) => {
      setDraft(value)
      mutate({ [field]: value })
    }

  return {
    vendor,
    setVendor,
    vendorError,
    saveVendor: () => {
      const value = vendor.trim()
      if (!vendorError && value !== (sentVendor.current ?? invoice.vendor)) {
        mutate({ vendor: value })
      }
    },
    categoryId,
    changeCategory: select("categoryId", setCategory),
    legCode,
    changeLegCode: select("legCode", setLegCode),
  }
}

const DetailField = ({
  label,
  htmlFor,
  children,
  className,
}: {
  label: string
  /** The input the label names, for an editable field */
  htmlFor?: string
  children: ReactNode
  className?: string
}) => (
  <div className={cn("flex min-w-0 flex-col gap-1.5", className)}>
    <dt className="text-muted-foreground text-sm">
      {/* Unbolded and on the dt's line height, so it matches the read-only labels beside it */}
      {htmlFor ? (
        <FieldLabel htmlFor={htmlFor} className="leading-[inherit] font-normal">
          {label}
        </FieldLabel>
      ) : (
        label
      )}
    </dt>
    {children}
  </div>
)

// The design's read-only inputs
const valueBox =
  "bg-muted/50 border-input min-h-8 rounded-lg border px-2.5 py-1.5 text-sm break-words"

const categoryLabel = ({ code, description }: { code: string; description: string }) =>
  `${code} - ${description}`

const ParsedInvoiceData = ({
  invoice,
  group,
  edit,
}: {
  invoice: InboxInvoice
  group?: CategoryGroup
  edit: InvoiceEdit
}) => {
  const { me } = useLoggedInUserInfo()
  const headingId = useId()
  const vendorId = useId()
  const vendorErrorId = useId()
  const categoryId = useId()
  const legCodeId = useId()

  const { data } = useQuery({
    queryKey: queryKeys.invoices.categories(me.organization.id),
    queryFn: () =>
      client.request(InvoiceCategoriesDocument, {
        input: { organizationId: me.organization.id },
      }),
  })
  const categories = (data?.invoiceCategoriesGet ?? []).toSorted((a, b) =>
    a.code.localeCompare(b.code),
  )
  // A deleted category is no longer offered, but still names the invoices filed under it
  const options =
    group?.categoryId && !categories.some(({ id }) => id === group.categoryId)
      ? [
          {
            id: group.categoryId,
            code: group.code ?? "",
            description: group.description ?? "",
          },
          ...categories,
        ]
      : categories
  const selected = options.find(({ id }) => id === edit.categoryId)

  return (
    <section aria-labelledby={headingId} className="flex flex-col gap-3">
      <h3 id={headingId} className="flex items-center gap-2 text-sm font-semibold">
        <FileText className="size-4" />
        Parsed Invoice Data
      </h3>
      {/* No <form>: inside one, Radix's hidden native select turns a value missing from its options into a pick of "" */}
      <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <DetailField label="PUR number">
          <dd className={valueBox}>{invoice.number}</dd>
        </DetailField>
        <DetailField label="Amount">
          <dd className={cn(valueBox, "tabular-nums")}>
            {formatAmount(invoice.total, invoice.currency)}
          </dd>
        </DetailField>
        <DetailField label="Vendor" htmlFor={vendorId}>
          <dd className="flex flex-col gap-1">
            <Input
              id={vendorId}
              value={edit.vendor}
              onChange={(event) => edit.setVendor(event.target.value)}
              onBlur={edit.saveVendor}
              onKeyDown={(event) => {
                if (event.key === "Enter" && !event.nativeEvent.isComposing) {
                  edit.saveVendor()
                }
              }}
              aria-invalid={edit.vendorError ? true : undefined}
              aria-describedby={edit.vendorError ? vendorErrorId : undefined}
            />
            <FieldError id={vendorErrorId}>{edit.vendorError}</FieldError>
          </dd>
        </DetailField>
        <DetailField label="Status">
          <dd className="flex min-h-8 items-center">
            <InvoiceTypeBadge invoice={invoice} />
          </dd>
        </DetailField>
        <DetailField label="Invoice date">
          <dd className={valueBox}>{invoice.invoiceDate.displaydate}</dd>
        </DetailField>
        {/* What makes it late, so it is tinted like the Late tag; others list it under More details */}
        {invoice.type === "LATE" && (
          <DetailField label="Service date">
            <dd
              className={cn(
                valueBox,
                "border-fuchsia-200 bg-fuchsia-50 dark:border-fuchsia-500/30 dark:bg-fuchsia-500/15",
              )}
            >
              {invoice.serviceDate.displaydate}
            </dd>
          </DetailField>
        )}
        {/* Always opens a row, so fields keep their place from one invoice to the next */}
        <DetailField label="Category" htmlFor={categoryId} className="sm:col-start-1">
          <dd>
            <Select value={edit.categoryId ?? ""} onValueChange={edit.changeCategory}>
              <SelectTrigger
                id={categoryId}
                size="sm"
                className="w-full min-w-0 rounded-lg px-2.5"
              >
                {/* Named here too, so it shows before the categories load */}
                <SelectValue placeholder="Uncategorized">
                  {selected && categoryLabel(selected)}
                </SelectValue>
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {options.map((category) => (
                  <SelectItem key={category.id} value={category.id}>
                    {categoryLabel(category)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </dd>
        </DetailField>
        {/* Late invoices are coded LI when posted */}
        {invoice.type === "LATE" ? (
          <DetailField label="Code">
            <dd className={valueBox}>{invoice.legCode ?? "—"}</dd>
          </DetailField>
        ) : (
          <DetailField label="Code" htmlFor={legCodeId}>
            <dd className="flex flex-col gap-1">
              <LegCodeSelect id={legCodeId} invoice={invoice} edit={edit} />
            </dd>
          </DetailField>
        )}
      </dl>
    </section>
  )
}

/** The legs the invoice can be filed under: its aircraft's arrivals in its service month. */
const LegCodeSelect = ({
  id,
  invoice,
  edit,
}: {
  id: string
  invoice: InboxInvoice
  edit: InvoiceEdit
}) => {
  const { me } = useLoggedInUserInfo()
  const errorId = useId()
  // The inbox lists the month's invoices by service month, so this is the invoice's
  const { year, month } = DateConverter.parsePeriod(route.useParams().period)!
  const organizationId = me.organization.id
  const aircraftId = invoice.aircraftId ?? null

  const { data, isLoading, isError, isFetching, refetch } = useQuery({
    queryKey: queryKeys.invoices.legCodes(organizationId, aircraftId, year, month),
    queryFn: () =>
      client.request(InvoiceLegCodesDocument, {
        input: { organizationId, aircraftId, year, month },
      }),
  })
  const legCodes = (data?.invoiceLegCodes ?? []).map(({ code, arrival, tails }) => ({
    code,
    // Without an aircraft the list is the whole fleet's, so it names the tails
    label: [code, arrival.displaydate, !invoice.aircraftId && tails.join(", ")]
      .filter(Boolean)
      .join(" · "),
  }))
  // A saved code drops out of the list when a cancelled leg renumbers the month
  const options =
    invoice.legCode && !legCodes.some(({ code }) => code === invoice.legCode)
      ? [{ code: invoice.legCode, label: invoice.legCode }, ...legCodes]
      : legCodes
  const selected = options.find(({ code }) => code === edit.legCode)
  const noLegs = !isLoading && !isError && options.length === 0

  return (
    <>
      <Select
        value={edit.legCode ?? ""}
        onValueChange={edit.changeLegCode}
        disabled={noLegs || isError}
      >
        <SelectTrigger
          id={id}
          size="sm"
          aria-describedby={isError ? errorId : undefined}
          className="w-full min-w-0 rounded-lg px-2.5"
        >
          {/* Named here too, so it shows before the legs load */}
          <SelectValue placeholder={noLegs ? "No legs this month" : "Select leg code"}>
            {selected?.label ?? edit.legCode}
          </SelectValue>
        </SelectTrigger>
        <SelectContent className="max-h-72">
          {options.map(({ code, label }) => (
            <SelectItem key={code} value={code}>
              {label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {isError && (
        <FieldError id={errorId} className="flex items-center gap-2">
          Couldn't load the leg codes.
          <Button
            variant="link"
            disabled={isFetching}
            onClick={() => void refetch()}
            className="h-auto p-0"
          >
            {isFetching ? "Retrying…" : "Retry"}
          </Button>
        </FieldError>
      )}
    </>
  )
}

const Field = ({ label, children }: { label: string; children: ReactNode }) => (
  <div className="flex justify-between gap-4 py-2 text-sm">
    <dt className="text-muted-foreground shrink-0">{label}</dt>
    <dd className="text-right break-words">{children}</dd>
  </div>
)

/** What the design leaves out of the form, so nothing shown before is lost. */
const MoreDetails = ({ invoice }: { invoice: InboxInvoice }) => (
  <Collapsible>
    <CollapsibleTrigger className="group text-muted-foreground hover:text-foreground flex items-center gap-1 text-sm font-medium">
      <ChevronDown className="size-4 transition-transform group-data-[state=open]:rotate-180" />
      More details
    </CollapsibleTrigger>
    <CollapsibleContent>
      <dl className="divide-y">
        {invoice.type !== "LATE" && (
          <Field label="Service date">{invoice.serviceDate.displaydate}</Field>
        )}
        <Field label="Subtotal">{formatAmount(invoice.subtotal, invoice.currency)}</Field>
        <Field label="VAT">{formatAmount(invoice.vat, invoice.currency)}</Field>
        <Field label="Due date">{invoice.dueDate?.displaydate ?? "—"}</Field>
        <Field label="Payment date">{invoice.paymentDate?.displaydate ?? "—"}</Field>
        <Field label="Airport">{invoice.airportCode ?? "—"}</Field>
        <Field label="Source">{invoice.source}</Field>
        {invoice.description && <Field label="Description">{invoice.description}</Field>}
        {invoice.notes && <Field label="Notes">{invoice.notes}</Field>}
      </dl>
    </CollapsibleContent>
  </Collapsible>
)
